import {
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	UpdateDrawerFormProps,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { useField } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataFieldType } from "../../../shared/types/dataField";
import { DataFieldTypeSelect } from "../datafields/DataFieldTypeSelect";
import { dataFieldService } from "../shared/api/dataFieldService";
import { DataFieldFormValues, DataFieldTranslationEntry, DataFieldUpdateResource } from "../shared/types/dataField";
import { hasDataFieldOptions, hasPlaceholder } from "../shared/utils/dataField";
import { DataFieldOpenOptionsButton } from "./DataFieldOpenOptionsButton";

const emptyTranslation = { label: "", description: null, placeholder: null };

const useValidate = () => {
	const { t } = useTranslation();

	return (values: DataFieldFormValues) => {
		const errors: { [K in keyof DataFieldFormValues]?: any } = {};

		return {
			...errors,
			...validateEachLanguage(values.translations, (_, values) => {
				const errors: { [K in keyof DataFieldTranslationEntry]?: any } = {};

				if (!values.label) {
					errors.label = t("Required field");
				}
				return errors;
			}),
		};
	};
};

interface Props {
	isCreate: boolean;
	onOpenOptions?: (locale: string) => void;
}

const DataFieldFormDrawerContent: FC<FormDrawerContentProps & Props> = ({ onOpenOptions, isCreate, ...props }) => {
	const [{ value: type }] = useField<DataFieldType>("type");
	const { t } = useTranslation();

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			{hasDataFieldOptions(type) && onOpenOptions && <DataFieldOpenOptionsButton onClick={onOpenOptions} />}
			<I18nTextField name="label" label={t("Label")} required={true} />
			<I18nOnlyOnMainForm>
				<DataFieldTypeSelect
					name="type"
					label={t("Type")}
					currentDataFieldType={!isCreate && type ? type : undefined}
				/>
			</I18nOnlyOnMainForm>
			<I18nTextField name="placeholder" label={t("Placeholder")} disabled={hasPlaceholder(type)} />
			<I18nTextField name="description" label={t("Description")} disabled={type === "checkbox"} />
		</I18nFormDrawerContent>
	);
};

export const DataFieldCreateDrawer: FC<CreateDrawerFormProps<DataFieldFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { languageSettings } = useDynamicConfig();
	const initialValues: DataFieldFormValues = useMemo(
		() => ({
			name: "",
			type: DataFieldType.TEXT,
			translations: [{ locale: languageSettings.fallback, ...emptyTranslation }],
		}),
		[languageSettings.fallback],
	);

	return (
		<I18nFormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Add data field")}
			validate={validate}
			renderForm={(props) => <DataFieldFormDrawerContent {...props} isCreate={true} />}
		/>
	);
};

interface DataFieldUpdateDrawerProps extends UpdateDrawerFormProps<DataFieldUpdateResource> {
	onOpenOptions: (locale: string) => void;
}

export const DataFieldUpdateDrawer: FC<DataFieldUpdateDrawerProps> = ({ onOpenOptions, ...drawerProps }) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...drawerProps}
			title={t("Edit data field")}
			queryFn={() => dataFieldService.show(drawerProps.id).then((x) => x.data.data)}
			queryKey={dataFieldService.showKey(drawerProps.id)}
			validate={validate}
			renderSuccess={(props) => (
				<DataFieldFormDrawerContent {...props} isCreate={false} onOpenOptions={onOpenOptions} />
			)}
		/>
	);
};
